<!--
  - Copyright (c) 2023. Haodong JU
  -->

<template>
  <div>
    <v-container>
      <ik-data-table
        ref="table"
        :model="model"
        entity-name="VoucherTypes"
        icon="mdi-cash-usd"
      >
        <template #item.imageDisplay="{item}">
          <div style="display: flex;">
            <img
              v-if="item.image"
              :src="item.image"
              alt="Image"
              style="width: 25px; height: 25px; margin-right: 5px;"
            >
          </div>
        </template>
        <template
          #item.checkDetail="{item}"
        >
          <v-btn
            :height="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
            :icon="!$vuetify.breakpoint.lgAndUp"
            :style="!$vuetify.breakpoint.lgAndUp ? 'border-radius: 12px; background: #f57c00;' : ''"
            :width="!$vuetify.breakpoint.lgAndUp ? '40px' : ''"
            color="amber lighten-4 black--text"
            elevation="0"
            small
            @click.stop="goto(item)"
          >
            <template v-if="!$vuetify.breakpoint.lgAndUp">
              <v-icon color="white">
                mdi-cog
              </v-icon>
            </template>
            <template v-if="$vuetify.breakpoint.lgAndUp">
              <div
                :class="!$vuetify.breakpoint.lgAndUp ? 'text-truncate' : ''"
                :style="!$vuetify.breakpoint.lgAndUp ? 'max-width: 100px;' : ''"
              >
                {{ $t('Details') }}
              </div>
            </template>
          </v-btn>
        </template>
      </ik-data-table>
      <v-dialog
        v-model="showVoucherCodeDialog"
        max-width="800px"
      >
        <v-card class="pa-4">
          <div
            v-if="selectedVoucherType"
            class="mb-4"
          >
            <v-card
              class="pa-3 mb-4"
              elevation="0"
            >
              <div class="d-flex align-center">
                <v-avatar
                  v-if="selectedVoucherType.image"
                  class="mr-3"
                  size="40"
                >
                  <v-img
                    :src="selectedVoucherType.image"
                    alt="Voucher Type Image"
                  />
                </v-avatar>
                <div>
                  <h2 class="text-h5 mb-1">
                    {{ selectedVoucherType.name }}
                  </h2>
                  <p
                    v-if="selectedVoucherType.description"
                    class="text-body-2 mb-0"
                  >
                    {{ selectedVoucherType.description }}
                  </p>
                </div>
              </div>
              <v-divider class="my-3" />
              <div>
                <template v-if="currentStep === 0">
                  <ik-data-table
                    ref="table"
                    :custom-on-row-click="editCode"
                    :filter="{ voucherTypeId: this.selectedVoucherType?.id }"
                    :item-per-page="5"
                    :model="codeModel"
                    :show-title="false"
                    :use-default-action="false"
                    entity-name="voucherCode"
                    icon="mdi-cash-usd"
                  >
                    <template #primaryButton>
                      <v-btn
                        color="amber lighten-4 black--text"
                        elevation="0"
                        @click="step = 1"
                      >
                        <v-icon left>
                          mdi-plus
                        </v-icon>
                        {{ $t('add') }}
                      </v-btn>
                    </template>
                  </ik-data-table>
                </template>
                <template v-if="currentStep === 1">
                  <v-form
                    ref="form"
                    v-model="valid"
                    @submit.prevent="saveVoucherCodes"
                  >
                    <v-card
                      class="pa-4"
                      elevation="0"
                    >
                      <v-row>
                        <v-col
                          v-if="!editMode"
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.prefix"
                            :label="$t('Prefix')"
                            dense
                            outlined
                          />
                        </v-col>
                        <v-col
                          v-if="!editMode"
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.count"
                            :label="$t('Count')"
                            dense
                            outlined
                            type="number"
                          />
                        </v-col>
                        <v-col
                          v-if="editMode"
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.code"
                            :label="$t('Code')"
                            dense
                            outlined
                          />
                        </v-col>
                        <v-col
                          v-if="editMode"
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.usageCount"
                            :label="$t('usageCount')"
                            dense
                            outlined
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.maxUsageCount"
                            :label="$t('Max Usage Count')"
                            dense
                            outlined
                            type="number"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-text-field
                            v-model="newVoucherCode.maxExchangePerUser"
                            :label="$t('Max Exchange Per User')"
                            dense
                            outlined
                            type="number"
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <DateSelector
                            v-model="newVoucherCode.expiredAt"
                            :dense="true"
                            :label="$t('Expired At')"
                            :min="today"
                            outlined
                          />
                        </v-col>
                        <v-col
                          cols="12"
                          sm="6"
                        >
                          <v-switch
                            v-model="newVoucherCode.active"
                            :label="$t('Active')"
                            class="mt-3"
                            color="primary"
                            hide-details
                          />
                        </v-col>
                      </v-row>

                      <v-card-actions class="pt-4">
                        <v-spacer />
                        <v-btn
                          color="grey lighten-1"
                          text
                          @click="step = 0"
                        >
                          {{ $t('Cancel') }}
                        </v-btn>
                        <v-btn
                          :disabled="!valid"
                          :loading="submitting"
                          color="primary"
                          @click="saveVoucherCodes"
                        >
                          {{ $t('Save') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-form>
                </template>
              </div>
              <v-divider class="my-3" />
              <div class="d-flex flex-wrap">
                <v-chip
                  class="ma-1"
                  color="blue-grey lighten-5"
                >
                  {{ $t('Discount') }}: {{ selectedVoucherType.discountStr }}
                </v-chip>
                <v-chip
                  class="ma-1"
                  color="blue-grey lighten-5"
                >
                  {{ $t('Min Amount') }}: {{ selectedVoucherType.usageMinAmount }}
                </v-chip>
                <v-chip
                  class="ma-1"
                  color="blue-grey lighten-5"
                >
                  {{ $t('Max Discount') }}: {{ selectedVoucherType.maxDiscountAmount }}
                </v-chip>
                <v-chip
                  class="ma-1"
                  color="blue-grey lighten-5"
                >
                  {{ $t('Valid From') }}: {{ selectedVoucherType.activeSinceDisplay }}
                </v-chip>
                <v-chip
                  class="ma-1"
                  color="blue-grey lighten-5"
                >
                  {{ $t('Valid Until') }}: {{ selectedVoucherType.expiredAtDisplay }}
                </v-chip>
              </div>
            </v-card>
          </div>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>

import IkDataTable from '@/plugins/EntityFrameWork/IkDataTable.vue'
import VoucherTypes from '@/model/dataLayer/api/Vip/VoucherTypes'
import VoucherCode from '@/model/dataLayer/api/Vip/VoucherCode'
import { VSwitch } from 'vuetify/lib/components'
import DateSelector from '@/views/AadenPages/settings/reserveSetting/openTime/DateSelector.vue'
import { today } from '@/model/utils/DateRepository'

export default {
  components: {
    IkDataTable,
    DateSelector,
    VSwitch
  },
  data: function () {
    return ({
      editMode: false,
      step: 0,
      showAddDialog: false,
      showVoucherCodeDialog: false,
      editIndex: -1,
      model: VoucherTypes,
      codeModel: VoucherCode,
      editItem: null,
      selectedVoucherType: null,
      loading: false,
      valid: true,
      submitting: false,
      newVoucherCode: {
        prefix: '',
        count: 1,
        maxUsageCount: 1,
        maxExchangePerUser: 1,
        expiredAt: null,
        active: true,
        voucherTypeId: null,
        usageCount: 0,
        code: ''
      }
    })
  },
  watch: {
    step: {
      deep: true,
      immediate: true,
      handler (val) {
        if (val === 0) {
          this.editMode = false
        }
      }
    }
  },
  computed: {
    currentStep () {
      return this.step
    },
    today () {
      return today
    }
  },
  methods: {
    editCode (item) {
      this.editMode = true
      this.newVoucherCode = Object.assign({}, item)
      this.step = 1
    },
    async saveVoucherCodes () {
      if (!this.$refs.form.validate()) return
      this.submitting = true
      try {
        if (!this.editMode) {
          this.newVoucherCode.voucherTypeId = this.selectedVoucherType.id
          await this.codeModel.add(this.newVoucherCode)
        } else {
          await this.codeModel.edit(this.newVoucherCode)
        }
        this.newVoucherCode = {
          prefix: '',
          count: 1,
          maxUsageCount: 1,
          maxExchangePerUser: 1,
          expiredAt: null,
          active: true,
          voucherTypeId: null,
          usageCount: 0,
          code: ''
        }
        this.step = 0
      } catch (error) {
        console.error('Error creating voucher codes:', error)
      } finally {
        this.submitting = false
      }
    },
    startEditImage (item) {
      this.editItem = item
      this.$refs.fileInput.$refs.input.click()
    },
    async goto (item) {
      this.selectedVoucherType = item
      this.showVoucherCodeDialog = true
    }
  }
}
</script>
